import gql from "graphql-tag";
import { AuthService } from "@/lib/auth";
import GraphQLClient, { RequestParam } from "../api";
import {
  AssistantCatMaster,
  AssistantCat,
  UpdateAssistantCatMasterInput,
  UpdateAssistantCatInput,
} from "../models/assistantCat";
import { logError, createApiLogContext } from "../utils/logger";

// ////////////////////
// Interface Definition
// ////////////////////

interface IAssistantCatClient {
  getAssistantCatMaster: () => Promise<AssistantCatMaster | null>;
  listAssistantCat: () => Promise<AssistantCat[]>;
  updateAssistantCatMaster: (input: UpdateAssistantCatMasterInput) => Promise<AssistantCatMaster>;
  updateAssistantCat: (input: UpdateAssistantCatInput) => Promise<AssistantCat>;
}

// ////////////////////
// AssistantCatClient Implementation
// ////////////////////

export class AssistantCatClient implements IAssistantCatClient {
  /**
   * アシスタント猫マスタデータを取得
   * @returns アシスタント猫マスタデータ（作家タイプ診断未受診の場合はnull）
   * @throws {Error} GraphQL APIエラー時
   * @example
   * const client = new AssistantCatClient();
   * const master = await client.getAssistantCatMaster();
   * if (master) {
   *   console.log(master.selectedType);
   * }
   */
  async getAssistantCatMaster(): Promise<AssistantCatMaster | null> {
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();
    const params: RequestParam = {
      query: getAssistantCatMasterQuery,
      headers: { "x-jwt-token": accessToken },
    };

    try {
      const { getAssistantCatMaster } = await GraphQLClient.request(params);
      return getAssistantCatMaster;
    } catch (error: unknown) {
      const context = createApiLogContext("AssistantCatClient", "getAssistantCatMaster");
      logError(context, "アシスタント猫マスタ取得エラー", error);
      throw error;
    }
  }

  /**
   * アシスタント猫の一覧を取得
   * @returns アシスタント猫の配列（作家タイプ診断未受診の場合は空配列）
   * @throws {Error} GraphQL APIエラー時
   * @example
   * const client = new AssistantCatClient();
   * const cats = await client.listAssistantCat();
   * const selectedCat = cats.find(cat => cat.selected);
   */
  async listAssistantCat(): Promise<AssistantCat[]> {
    const params: RequestParam = {
      query: listAssistantCatQuery,
    };

    try {
      const { listAssistantCat } = await GraphQLClient.request(params);
      return listAssistantCat || [];
    } catch (error: unknown) {
      const context = createApiLogContext("AssistantCatClient", "listAssistantCat");
      logError(context, "アシスタント猫一覧取得エラー", error);
      throw error;
    }
  }

  /**
   * アシスタント猫マスタを更新（選択中の猫を切り替え）
   * @param input 更新内容（selectedType）
   * @returns 更新後のアシスタント猫マスタデータ
   * @throws {Error} GraphQL APIエラー時
   * @example
   * const client = new AssistantCatClient();
   * const updatedMaster = await client.updateAssistantCatMaster({
   *   selectedType: AssistWriterType.ELAS
   * });
   */
  async updateAssistantCatMaster(input: UpdateAssistantCatMasterInput): Promise<AssistantCatMaster> {
    const params: RequestParam = {
      query: updateAssistantCatMasterMutation,
      variables: {
        input,
      },
    };

    try {
      const { updateAssistantCatMaster } = await GraphQLClient.request(params);
      return updateAssistantCatMaster;
    } catch (error: unknown) {
      const context = createApiLogContext("AssistantCatClient", "updateAssistantCatMaster");
      logError(context, "アシスタント猫マスタ更新エラー", error);
      throw error;
    }
  }

  /**
   * アシスタント猫の情報を更新（ニックネーム変更）
   * @param input 更新内容（assistantType, name）
   * @returns 更新後のアシスタント猫データ
   * @throws {Error} GraphQL APIエラー時
   * @example
   * const client = new AssistantCatClient();
   * const updatedCat = await client.updateAssistantCat({
   *   assistantType: AssistWriterType.ELAH,
   *   name: "任意のニックネーム"
   * });
   */
  async updateAssistantCat(input: UpdateAssistantCatInput): Promise<AssistantCat> {
    const params: RequestParam = {
      query: updateAssistantCatMutation,
      variables: {
        input,
      },
    };

    try {
      const { updateAssistantCat } = await GraphQLClient.request(params);
      return updateAssistantCat;
    } catch (error: unknown) {
      const context = createApiLogContext("AssistantCatClient", "updateAssistantCat");
      logError(context, "アシスタント猫情報更新エラー", error);
      throw error;
    }
  }
}

// ////////////////////
// GraphQL Queries & Mutations
// ////////////////////

const getAssistantCatMasterQuery = gql`
  query GetAssistantCatMaster {
    getAssistantCatMaster {
      userId
      selectedType
      writerType
      serializedResult
      createdAt
      updatedAt
    }
  }
`;

const listAssistantCatQuery = gql`
  query ListAssistantCat {
    listAssistantCat {
      userId
      assistantCatType
      name
      officialName
      officialNameJapanese
      description
      group
      groupName
      selected
      isWriterType
      isSynergyType
      isImpactType
      reactionAfterSetName
      createdAt
      updatedAt
    }
  }
`;

const updateAssistantCatMasterMutation = gql`
  mutation UpdateAssistantCatMaster($input: UpdateAssistantCatMasterInput!) {
    updateAssistantCatMaster(input: $input) {
      userId
      selectedType
      writerType
      serializedResult
      createdAt
      updatedAt
    }
  }
`;

const updateAssistantCatMutation = gql`
  mutation UpdateAssistantCat($input: UpdateAssistantCatInput!) {
    updateAssistantCat(input: $input) {
      userId
      assistantCatType
      name
      officialName
      officialNameJapanese
      description
      group
      groupName
      selected
      isWriterType
      isSynergyType
      isImpactType
      createdAt
      updatedAt
    }
  }
`;
